import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  computed,
  HostBinding,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { QUINNY_AI_LOGO_TRANSPARENT_URL } from '@core';
import { FeatureStateService } from 'src/app/shared/services/feature-state.service';
import {
  HowToService,
  HowToArticle,
  HowToArticlesByScope
} from 'src/app/shared/services/how-to.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzFloatButtonModule } from 'ng-zorro-antd/float-button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  HOW_TO_SCOPE_KEY,
  getHowToScopesFromRoute
} from 'src/app/shared/utils/how-to-route.util';
import { PageHowToToolbarService } from './page-how-to-toolbar.service';

export { HOW_TO_SCOPE_KEY };

const HOW_TO_DRAWER_WIDTH_STORAGE_KEY = 'page-how-to-drawer-width';
const HOW_TO_DRAWER_WIDTH_DEFAULT = 640;
const HOW_TO_DRAWER_WIDTH_MIN = 360;
const HOW_TO_DRAWER_WIDTH_MAX_CAP = 1200;

function readStoredHowToDrawerWidth(): number {
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = localStorage.getItem(HOW_TO_DRAWER_WIDTH_STORAGE_KEY);
      const n = raw != null ? parseInt(raw, 10) : NaN;
      if (Number.isFinite(n) && n > 0) return n;
    } catch {
      /* private mode */
    }
  }
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const fromViewport = Math.round(
    Math.min(560, Math.max(HOW_TO_DRAWER_WIDTH_MIN, vw * 0.28))
  );
  return Number.isFinite(fromViewport) ? fromViewport : HOW_TO_DRAWER_WIDTH_DEFAULT;
}

@Component({
  selector: 'app-page-how-to',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzCollapseModule,
    NzFloatButtonModule,
    NzIconModule,
    NzSpinModule,
    NzToolTipModule
  ],
  templateUrl: './page-how-to.component.html',
  styleUrls: ['./page-how-to.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageHowToComponent implements OnInit, OnDestroy {
  private static exploreSparkleGradientSeq = 0;

  @ViewChild('howToPanelRoot', { read: ElementRef })
  private howToPanelRoot?: ElementRef<HTMLElement>;

  /** Unique per instance so SVG `fill="url(#…)"` does not collide across multiple drawers. */
  readonly exploreSparkleGradientId = `how-to-explore-sparkle-grad-${PageHowToComponent.exploreSparkleGradientSeq++}`;

  private router = inject(Router);
  private howToService = inject(HowToService);
  private featureState = inject(FeatureStateService);
  private pageHowToToolbar = inject(PageHowToToolbarService);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private doc = inject(DOCUMENT);
  private destroy$ = new Subject<void>();
  /** Ignores stale HTTP responses when a newer load was started. */
  private articlesLoadGeneration = 0;

  /**
   * Panel is in the DOM (fixed rail from top of viewport beside header + content; not inside reuse-tab / router / footer).
   */
  drawerVisible = signal(false);
  /**
   * When true, the drawer is hidden but a floating control remains so the user can reopen
   * without losing loaded articles.
   */
  drawerMinimizedToFloating = signal(false);
  /** Drawer width (px); persisted while dragging ends / keyboard adjust. */
  drawerWidthPx = signal(readStoredHowToDrawerWidth());
  loading = signal(false);
  articleSections = signal<HowToArticlesByScope[]>([]);
  scopes = signal<string[]>([]);

  /** Collapse panel open state keyed by articleUrl */
  panelActive = signal<Record<string, boolean>>({});
  articleHtmlByUrl = signal<Record<string, SafeHtml | null>>({});
  articleLoadingByUrl = signal<Record<string, boolean>>({});
  articleErrorByUrl = signal<Record<string, boolean>>({});

  isInternal = computed(() => this.featureState.isInternal());

  @HostBinding('style.width.px')
  get howToRailHostWidth(): number {
    if (!this.isInternal()) return 0;
    if (!this.drawerVisible() || this.drawerMinimizedToFloating()) return 0;
    return this.drawerWidthPx();
  }

  @HostBinding('class.page-how-to-host--open')
  get howToRailHostOpen(): boolean {
    return (
      this.isInternal() &&
      this.drawerVisible() &&
      !this.drawerMinimizedToFloating()
    );
  }

  totalArticleCount = computed(() =>
    this.articleSections().reduce((n, s) => n + s.articles.length, 0)
  );

  supportUrl = computed(() => {
    const country = this.howToService.getCountry();
    return country === 'MY'
      ? 'https://support.qne.com.my'
      : 'https://support.qne.com.ph';
  });

  /** Expand/collapse chevron on the left (LTR: `start`). */
  readonly howToCollapseExpandIconPosition: 'start' | 'end' = 'start';

  /** Transparent mark so the banner gradient shows through (`Quinny-Ai-Logo-Transparent.png`). */
  readonly quinnyAiBannerLogoUrl = QUINNY_AI_LOGO_TRANSPARENT_URL;

  readonly howToDrawerMinWidthPx = HOW_TO_DRAWER_WIDTH_MIN;

  constructor() {
    effect(() => {
      const w =
        this.isInternal() &&
        this.drawerVisible() &&
        !this.drawerMinimizedToFloating()
          ? this.drawerWidthPx()
          : 0;
      const html = this.doc.documentElement;
      if (w > 0) {
        html.style.setProperty('--how-to-rail-width', `${w}px`);
        html.classList.add('how-to-rail-open');
      } else {
        html.style.removeProperty('--how-to-rail-width');
        html.classList.remove('how-to-rail-open');
      }
    });
  }

  ngOnInit(): void {
    this.drawerWidthPx.set(this.clampDrawerWidth(this.drawerWidthPx()));
    this.updateScope();
    this.pageHowToToolbar.openRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.openDrawer();
        this.cdr.markForCheck();
      });
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.updateScope());
  }

  ngOnDestroy(): void {
    const body = this.doc.body;
    if (body) body.style.overflow = '';
    const html = this.doc.documentElement;
    html.style.removeProperty('--how-to-rail-width');
    html.classList.remove('how-to-rail-open');
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscape(e: Event): void {
    if (!(e instanceof KeyboardEvent)) return;
    if (!this.drawerVisible() || this.drawerMinimizedToFloating()) return;
    const panel = this.howToPanelRoot?.nativeElement;
    const t = e.target;
    if (!(t instanceof Node) || !panel?.contains(t)) return;
    e.preventDefault();
    this.closeDrawer();
  }

  /** Side-by-side mode: do not lock body scroll so the main app stays usable next to the panel. */
  private syncHowToPanelBodyScrollLock(): void {
    const body = this.doc.body;
    if (body) body.style.overflow = '';
  }

  private updateScope(): void {
    // While the panel is open (expanded or minimized to FAB), keep the current
    // articles/scopes until the user closes — do not follow route changes.
    if (this.drawerVisible()) {
      return;
    }
    const next = getHowToScopesFromRoute(this.router);
    const prev = this.scopes();
    const scopesChanged =
      prev.length !== next.length ||
      prev.some((s, i) => s !== next[i]);
    this.scopes.set(next);
    if (scopesChanged) {
      this.drawerMinimizedToFloating.set(false);
      this.syncHowToPanelBodyScrollLock();
    }
  }

  openDrawer(): void {
    if (this.drawerMinimizedToFloating()) {
      this.drawerMinimizedToFloating.set(false);
      this.drawerWidthPx.set(this.clampDrawerWidth(this.drawerWidthPx()));
      this.syncHowToPanelBodyScrollLock();
      this.loadArticlesForCurrentScopes();
      this.cdr.markForCheck();
      return;
    }
    this.drawerWidthPx.set(this.clampDrawerWidth(this.drawerWidthPx()));
    const becomingVisible = !this.drawerVisible();
    this.drawerVisible.set(true);
    this.syncHowToPanelBodyScrollLock();
    if (becomingVisible) {
      this.loadArticlesForCurrentScopes();
    }
    this.cdr.markForCheck();
  }

  private loadArticlesForCurrentScopes(): void {
    const gen = ++this.articlesLoadGeneration;
    const scopeList = this.scopes();
    this.articleSections.set([]);
    this.panelActive.set({});
    if (scopeList.length === 0) {
      this.loading.set(false);
      this.cdr.markForCheck();
      return;
    }
    this.loading.set(true);
    this.howToService.getArticlesGroupedByScope(scopeList).subscribe({
      next: (sections) => {
        if (gen !== this.articlesLoadGeneration) return;
        this.articleSections.set(sections);
        this.loading.set(false);
        const first = this.firstArticleInOrder(sections);
        if (first?.articleUrl) {
          this.panelActive.set({ [first.articleUrl]: true });
          this.loadArticleHtmlIfNeeded(first);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        if (gen !== this.articlesLoadGeneration) return;
        this.articleSections.set([]);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  closeDrawer(): void {
    this.drawerVisible.set(false);
    this.drawerMinimizedToFloating.set(false);
    this.panelActive.set({});
    this.syncHowToPanelBodyScrollLock();
    this.scopes.set(getHowToScopesFromRoute(this.router));
  }

  /** Hide the panel visually and show the FAB; DOM stays mounted (no nz-drawer teardown). */
  collapseHowToToFloating(): void {
    this.drawerMinimizedToFloating.set(true);
    this.syncHowToPanelBodyScrollLock();
    queueMicrotask(() => {
      const a = this.doc.activeElement as HTMLElement | null;
      a?.blur?.();
    });
  }

  onPanelActiveChange(a: HowToArticle, open: boolean): void {
    const u = a.articleUrl;
    if (!u) return;
    this.panelActive.update((m) => ({ ...m, [u]: open }));
    if (!open) return;
    this.loadArticleHtmlIfNeeded(a);
  }

  /** First article in API/display order (used to auto-expand one panel). */
  private firstArticleInOrder(
    sections: HowToArticlesByScope[]
  ): HowToArticle | null {
    for (const s of sections) {
      if (s.articles.length > 0) return s.articles[0];
    }
    return null;
  }

  private loadArticleHtmlIfNeeded(a: HowToArticle): void {
    const u = a.articleUrl;
    if (!u) return;
    if (this.articleHtmlByUrl()[u] != null) return;
    if (this.articleLoadingByUrl()[u]) return;

    this.articleLoadingByUrl.update((m) => ({ ...m, [u]: true }));
    this.articleErrorByUrl.update((m) => ({ ...m, [u]: false }));

    this.howToService.getArticleHtml(u).subscribe({
      next: (html) => {
        const safe = html
          ? this.sanitizer.bypassSecurityTrustHtml(html)
          : null;
        this.articleHtmlByUrl.update((m) => ({ ...m, [u]: safe }));
        this.articleLoadingByUrl.update((m) => ({ ...m, [u]: false }));
        if (!html) {
          this.articleErrorByUrl.update((m) => ({ ...m, [u]: true }));
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.articleLoadingByUrl.update((m) => ({ ...m, [u]: false }));
        this.articleErrorByUrl.update((m) => ({ ...m, [u]: true }));
        this.cdr.markForCheck();
      }
    });
  }

  isPanelActive(articleUrl: string): boolean {
    return !!this.panelActive()[articleUrl];
  }

  isArticleLoading(articleUrl: string): boolean {
    return !!this.articleLoadingByUrl()[articleUrl];
  }

  articleHtml(articleUrl: string): SafeHtml | null {
    return this.articleHtmlByUrl()[articleUrl] ?? null;
  }

  articleHasError(articleUrl: string): boolean {
    return !!this.articleErrorByUrl()[articleUrl];
  }

  openArticle(url: string): void {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Header extra control: avoid toggling the panel when opening the article. */
  openArticleExternal(event: Event, url: string): void {
    event.stopPropagation();
    this.openArticle(url);
  }

  openExploreMore(): void {
    const url = this.supportUrl();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  private drawerSplitDrag:
    | { pointerId: number; startClientX: number; startWidth: number }
    | null = null;

  /** Upper bound for current viewport (for clamp + ARIA). */
  drawerWidthMax(): number {
    const cap = Math.min(
      HOW_TO_DRAWER_WIDTH_MAX_CAP,
      (typeof window !== 'undefined' ? window.innerWidth : HOW_TO_DRAWER_WIDTH_MAX_CAP) -
        64
    );
    return Math.max(HOW_TO_DRAWER_WIDTH_MIN, cap);
  }

  private clampDrawerWidth(w: number): number {
    return Math.round(
      Math.min(this.drawerWidthMax(), Math.max(HOW_TO_DRAWER_WIDTH_MIN, w))
    );
  }

  onDrawerSplitterPointerDown(e: PointerEvent): void {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.drawerSplitDrag = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startWidth: this.drawerWidthPx()
    };
  }

  onDrawerSplitterPointerMove(e: PointerEvent): void {
    const d = this.drawerSplitDrag;
    if (!d || e.pointerId !== d.pointerId) return;
    const next = d.startWidth + (d.startClientX - e.clientX);
    this.drawerWidthPx.set(this.clampDrawerWidth(next));
    this.cdr.markForCheck();
  }

  onDrawerSplitterPointerUp(e: PointerEvent): void {
    const d = this.drawerSplitDrag;
    if (!d || e.pointerId !== d.pointerId) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    this.drawerSplitDrag = null;
    try {
      localStorage.setItem(
        HOW_TO_DRAWER_WIDTH_STORAGE_KEY,
        String(this.drawerWidthPx())
      );
    } catch {
      /* private mode */
    }
  }

  /** Right-side drawer: ArrowLeft widens, ArrowRight narrows. */
  onDrawerSplitterKeydown(e: KeyboardEvent): void {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = e.shiftKey ? 40 : 16;
    const delta = e.key === 'ArrowLeft' ? step : -step;
    this.drawerWidthPx.update((w) => this.clampDrawerWidth(w + delta));
    try {
      localStorage.setItem(
        HOW_TO_DRAWER_WIDTH_STORAGE_KEY,
        String(this.drawerWidthPx())
      );
    } catch {
      /* private mode */
    }
    this.cdr.markForCheck();
  }
}
